import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import type { SentryConfig } from './types'

const SAMPLE_RATE_NONE = 0
const SAMPLE_RATE_TEN_PERCENT = 0.1
const SAMPLE_RATE_FULL = 1.0

export const REPLAY_PRIVACY_OPTIONS = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
} as const

export function initSentry(config: SentryConfig): void {
  if (!config.dsn) {
    return
  }

  const { isDev } = config

  Sentry.init({
    dsn: config.dsn,
    environment: isDev ? 'development' : 'production',
    release: config.release,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(REPLAY_PRIVACY_OPTIONS),
    ],
    tracesSampleRate: isDev ? SAMPLE_RATE_NONE : SAMPLE_RATE_TEN_PERCENT,
    replaysSessionSampleRate: isDev ? SAMPLE_RATE_NONE : SAMPLE_RATE_TEN_PERCENT,
    replaysOnErrorSampleRate: SAMPLE_RATE_FULL,
    beforeSend(event, hint) {
      if (isDev) {
        const error = hint.originalException
        if (error instanceof Error && error.message.includes('Network Error')) {
          return null
        }
      }
      return event
    },
    beforeBreadcrumb(breadcrumb) {
      if (!isDev && breadcrumb.category === 'console') {
        return null
      }
      return breadcrumb
    },
  })

  Sentry.setContext('app', {
    name: config.appName,
    version: config.version,
  })
}
